import * as THREE from 'three';
import {
  GROUND_SIZE,
  TREE_CANOPY_COLOR, TREE_TRUNK_COLOR,
} from '@/utils/constants';

// ── Bright day palette ──────────────────────────────────────
const SKY_TOP_COLOR = 0x4a90d9;    // Deep sky blue
const SKY_MID_COLOR = 0x87ceeb;    // Light sky blue
const SKY_BOTTOM_COLOR = 0xc9e8f7; // Pale horizon blue
const GROUND_LIGHT_GREEN = 0x5aaa3a; // Bright grass
const GROUND_DARK_GREEN = 0x3d8a2a;  // Darker grass patches
const LAKE_COLOR = 0x4a8ab5;
const LAKE_SHORE_COLOR = 0xc4a86a;
const DUCK_BODY_COLOR = 0xf5f0e0;
const DUCK_HEAD_COLOR = 0x2a7a3a;
const DUCK_BEAK_COLOR = 0xe8a020;
const CLOUD_COLOR = 0xffffff;
const FLOWER_COLORS = [0xff6b8a, 0xffd93d, 0xc084fc, 0x6ee7b7, 0xf472b6];
const PATH_COLOR = 0xd4b896;

// Tree positions — nicely framing the building, leaving front clear
const TREE_POSITIONS: [number, number, number, number][] = [
  // [x, z, scale, type] — type: 0=conifer, 1=round
  [-5, -3, 1.2, 1],
  [-4, 3, 0.9, 0],
  [5.5, -2, 1.1, 1],
  [6.5, 3.5, 1.0, 0],
  [-6, 5, 0.8, 1],
  [4.5, -5, 1.3, 0],
  [-3, -6, 1.0, 1],
  [7.5, 0, 0.7, 0],
  [-7, 1, 1.1, 0],
  [3, 6, 0.9, 1],
];

/**
 * Creates a beautiful sunny-day environment: bright sky, lush ground,
 * a lake with ducks, clouds, flowers, birds, and cozy trees.
 */
export function createEnvironment(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'environment';

  group.add(createSkyDome());
  group.add(createGround());
  group.add(createLake());
  group.add(createPath());
  group.add(createClouds());

  // Trees
  for (let i = 0; i < TREE_POSITIONS.length; i++) {
    const [x, z, scale, type] = TREE_POSITIONS[i];
    const tree = type === 0 ? createConiferTree() : createRoundTree();
    tree.position.set(x, 0, z);
    tree.scale.setScalar(scale);
    tree.name = `tree-${i}`;
    group.add(tree);
  }

  // Ducks on the lake
  group.add(createDucks());

  // Birds in the sky
  group.add(createBirds());

  // Flower patches
  group.add(createFlowers());

  // Small bushes
  group.add(createBushes());

  return group;
}

// ── Sky Dome ────────────────────────────────────────────────
function createSkyDome(): THREE.Mesh {
  const geo = new THREE.SphereGeometry(45, 32, 32);
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      topColor: { value: new THREE.Color(SKY_TOP_COLOR) },
      midColor: { value: new THREE.Color(SKY_MID_COLOR) },
      bottomColor: { value: new THREE.Color(SKY_BOTTOM_COLOR) },
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPosition = wp.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 topColor;
      uniform vec3 midColor;
      uniform vec3 bottomColor;
      varying vec3 vWorldPosition;
      void main() {
        float h = normalize(vWorldPosition).y;
        vec3 color;
        if (h > 0.2) {
          color = mix(midColor, topColor, smoothstep(0.2, 0.7, h));
        } else {
          color = mix(bottomColor, midColor, smoothstep(-0.1, 0.2, h));
        }
        gl_FragColor = vec4(color, 1.0);
      }
    `,
    side: THREE.BackSide,
    depthWrite: false,
  });
  const sky = new THREE.Mesh(geo, mat);
  sky.name = 'sky-dome';
  return sky;
}

// ── Ground ──────────────────────────────────────────────────
function createGround(): THREE.Group {
  const g = new THREE.Group();

  // Main ground
  const groundGeo = new THREE.PlaneGeometry(GROUND_SIZE, GROUND_SIZE, 1, 1);
  const groundMat = new THREE.MeshStandardMaterial({ color: GROUND_LIGHT_GREEN, roughness: 0.8 });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  ground.name = 'ground';
  g.add(ground);

  // Gentle rolling hills in background
  const hillMat = new THREE.MeshStandardMaterial({ color: GROUND_DARK_GREEN, roughness: 0.9 });
  const hillPositions: [number, number, number, number][] = [
    [-8, -10, 3.5, 1.5],
    [0, -12, 5, 2],
    [10, -11, 4, 1.8],
    [-12, -8, 3, 1.2],
    [12, -9, 2.5, 1.0],
  ];
  hillPositions.forEach(([x, z, rx, ry], i) => {
    const hillGeo = new THREE.SphereGeometry(rx, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
    const hill = new THREE.Mesh(hillGeo, hillMat);
    hill.position.set(x, 0, z);
    hill.scale.y = ry / rx;
    hill.receiveShadow = true;
    hill.name = `hill-${i}`;
    g.add(hill);
  });

  return g;
}

// ── Lake ────────────────────────────────────────────────────
function createLake(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'lake';

  // Lake water — elliptical shape
  const lakeGeo = new THREE.CircleGeometry(3.5, 32);
  const lakeMat = new THREE.MeshStandardMaterial({
    color: LAKE_COLOR,
    roughness: 0.1,
    metalness: 0.2,
    transparent: true,
    opacity: 0.85,
  });
  const lake = new THREE.Mesh(lakeGeo, lakeMat);
  lake.rotation.x = -Math.PI / 2;
  lake.position.set(-8, 0.02, 3);
  lake.scale.set(1, 0.7, 1);
  lake.receiveShadow = true;
  g.add(lake);

  // Shore ring
  const shoreGeo = new THREE.RingGeometry(3.3, 3.8, 32);
  const shoreMat = new THREE.MeshStandardMaterial({ color: LAKE_SHORE_COLOR, roughness: 0.9 });
  const shore = new THREE.Mesh(shoreGeo, shoreMat);
  shore.rotation.x = -Math.PI / 2;
  shore.position.set(-8, 0.015, 3);
  shore.scale.set(1, 0.7, 1);
  g.add(shore);

  return g;
}

// ── Ducks ───────────────────────────────────────────────────
function createDucks(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'ducks';

  const duckPositions: [number, number][] = [
    [-7.5, 2.5],
    [-8.8, 3.5],
    [-7.2, 4.0],
  ];

  duckPositions.forEach(([x, z], i) => {
    const duck = createDuck();
    duck.position.set(x, 0.08, z);
    duck.rotation.y = Math.random() * Math.PI * 2;
    duck.name = `duck-${i}`;
    duck.userData = { animOffset: Math.random() * Math.PI * 2 };
    g.add(duck);
  });

  return g;
}

function createDuck(): THREE.Group {
  const d = new THREE.Group();

  // Body
  const bodyGeo = new THREE.SphereGeometry(0.15, 8, 8);
  const bodyMat = new THREE.MeshStandardMaterial({ color: DUCK_BODY_COLOR });
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.scale.set(1, 0.7, 1.3);
  body.castShadow = true;
  d.add(body);

  // Head
  const headGeo = new THREE.SphereGeometry(0.08, 8, 8);
  const headMat = new THREE.MeshStandardMaterial({ color: DUCK_HEAD_COLOR });
  const head = new THREE.Mesh(headGeo, headMat);
  head.position.set(0, 0.1, 0.15);
  d.add(head);

  // Beak
  const beakGeo = new THREE.ConeGeometry(0.03, 0.06, 4);
  const beakMat = new THREE.MeshStandardMaterial({ color: DUCK_BEAK_COLOR });
  const beak = new THREE.Mesh(beakGeo, beakMat);
  beak.position.set(0, 0.08, 0.23);
  beak.rotation.x = -Math.PI / 2;
  d.add(beak);

  return d;
}

// ── Birds ───────────────────────────────────────────────────
function createBirds(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'birds';

  // Simple V-shapes in the sky
  const birdPositions: [number, number, number][] = [
    [5, 12, -5],
    [8, 14, -3],
    [6, 13, -4],
    [-3, 11, -8],
    [-5, 13, -6],
  ];

  const birdMat = new THREE.MeshBasicMaterial({ color: 0x333333 });

  birdPositions.forEach(([x, y, z], i) => {
    const bird = new THREE.Group();
    bird.name = `bird-${i}`;
    bird.userData = { animOffset: Math.random() * Math.PI * 2, speed: 0.5 + Math.random() * 0.5 };

    // Two tiny wing triangles forming a V
    const wingGeo = new THREE.PlaneGeometry(0.3, 0.06);
    const leftWing = new THREE.Mesh(wingGeo, birdMat);
    leftWing.position.x = -0.12;
    leftWing.rotation.z = 0.3;
    bird.add(leftWing);

    const rightWing = new THREE.Mesh(wingGeo, birdMat);
    rightWing.position.x = 0.12;
    rightWing.rotation.z = -0.3;
    bird.add(rightWing);

    bird.position.set(x, y, z);
    bird.lookAt(x + 1, y, z - 0.5);
    g.add(bird);
  });

  return g;
}

// ── Clouds ──────────────────────────────────────────────────
function createClouds(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'clouds';

  const cloudMat = new THREE.MeshStandardMaterial({
    color: CLOUD_COLOR,
    roughness: 1.0,
    emissive: CLOUD_COLOR,
    emissiveIntensity: 0.15,
  });

  const cloudConfigs: [number, number, number, number][] = [
    [-10, 10, -8, 1.2],
    [8, 11, -10, 0.9],
    [0, 12, -12, 1.4],
    [-5, 9, -6, 0.7],
    [12, 10, -5, 1.0],
  ];

  cloudConfigs.forEach(([x, y, z, scale], i) => {
    const cloud = new THREE.Group();
    cloud.name = `cloud-${i}`;
    cloud.userData = { animOffset: Math.random() * 100 };

    // Cluster of 3-5 spheres
    const numPuffs = 3 + Math.floor(Math.random() * 3);
    for (let p = 0; p < numPuffs; p++) {
      const r = 0.5 + Math.random() * 0.5;
      const puffGeo = new THREE.SphereGeometry(r, 8, 6);
      const puff = new THREE.Mesh(puffGeo, cloudMat);
      puff.position.set(
        (Math.random() - 0.5) * 1.5,
        (Math.random() - 0.5) * 0.3,
        (Math.random() - 0.5) * 0.8,
      );
      cloud.add(puff);
    }

    cloud.position.set(x, y, z);
    cloud.scale.setScalar(scale);
    g.add(cloud);
  });

  return g;
}

// ── Flowers ─────────────────────────────────────────────────
function createFlowers(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'flowers';

  // Scatter small flower clusters
  for (let i = 0; i < 25; i++) {
    const x = (Math.random() - 0.5) * 20;
    const z = (Math.random() - 0.5) * 20;

    // Skip if too close to building or lake
    if (Math.abs(x) < 3 && Math.abs(z) < 3) continue;
    if (Math.sqrt((x + 8) ** 2 + (z - 3) ** 2) < 4.5) continue;

    const color = FLOWER_COLORS[Math.floor(Math.random() * FLOWER_COLORS.length)];
    const flowerGeo = new THREE.SphereGeometry(0.05 + Math.random() * 0.04, 6, 4);
    const flowerMat = new THREE.MeshStandardMaterial({ color });
    const flower = new THREE.Mesh(flowerGeo, flowerMat);
    flower.position.set(x, 0.05, z);
    g.add(flower);

    // Tiny green stem
    const stemGeo = new THREE.CylinderGeometry(0.008, 0.008, 0.05, 4);
    const stemMat = new THREE.MeshStandardMaterial({ color: 0x3a8a2a });
    const stem = new THREE.Mesh(stemGeo, stemMat);
    stem.position.set(x, 0.025, z);
    g.add(stem);
  }

  return g;
}

// ── Path ────────────────────────────────────────────────────
function createPath(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'path';

  // Stepping stones leading to the building door
  const pathMat = new THREE.MeshStandardMaterial({ color: PATH_COLOR, roughness: 0.7 });
  for (let i = 0; i < 6; i++) {
    const stoneGeo = new THREE.CylinderGeometry(0.25 + Math.random() * 0.1, 0.25, 0.04, 8);
    const stone = new THREE.Mesh(stoneGeo, pathMat);
    stone.position.set(0 + Math.sin(i * 0.3) * 0.15, 0.02, 1.5 + i * 0.8);
    stone.rotation.y = Math.random() * 0.5;
    stone.receiveShadow = true;
    g.add(stone);
  }

  return g;
}

// ── Bushes ──────────────────────────────────────────────────
function createBushes(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'bushes';

  const bushMat = new THREE.MeshStandardMaterial({ color: 0x3a8a3a });
  const bushPositions: [number, number, number][] = [
    [-2.5, 0, 1.5],
    [2.5, 0, 1.5],
    [-1.5, 0, -2.5],
    [1.5, 0, -2.5],
  ];

  bushPositions.forEach(([x, y, z], i) => {
    const bush = new THREE.Group();
    for (let j = 0; j < 3; j++) {
      const r = 0.2 + Math.random() * 0.15;
      const bGeo = new THREE.SphereGeometry(r, 8, 6);
      const b = new THREE.Mesh(bGeo, bushMat);
      b.position.set((Math.random() - 0.5) * 0.2, r * 0.7, (Math.random() - 0.5) * 0.2);
      b.castShadow = true;
      bush.add(b);
    }
    bush.position.set(x, y, z);
    bush.name = `bush-${i}`;
    g.add(bush);
  });

  return g;
}

// ── Trees ───────────────────────────────────────────────────
function createConiferTree(): THREE.Group {
  const tree = new THREE.Group();
  const trunkMat = new THREE.MeshStandardMaterial({ color: TREE_TRUNK_COLOR });
  const canopyMat = new THREE.MeshStandardMaterial({ color: TREE_CANOPY_COLOR });

  const trunkGeo = new THREE.CylinderGeometry(0.12, 0.15, 1.0, 8);
  const trunk = new THREE.Mesh(trunkGeo, trunkMat);
  trunk.position.y = 0.5;
  trunk.castShadow = true;
  tree.add(trunk);

  // 2-3 layered cones for a fuller look
  const layers = [
    { y: 1.3, radius: 0.9, height: 1.2 },
    { y: 2.0, radius: 0.65, height: 1.0 },
    { y: 2.5, radius: 0.4, height: 0.8 },
  ];
  layers.forEach((l) => {
    const coneGeo = new THREE.ConeGeometry(l.radius, l.height, 7);
    const cone = new THREE.Mesh(coneGeo, canopyMat);
    cone.position.y = l.y;
    cone.castShadow = true;
    tree.add(cone);
  });

  return tree;
}

function createRoundTree(): THREE.Group {
  const tree = new THREE.Group();
  const trunkMat = new THREE.MeshStandardMaterial({ color: TREE_TRUNK_COLOR });
  const canopyMat = new THREE.MeshStandardMaterial({ color: 0x4aaa3a }); // Slightly brighter green

  const trunkGeo = new THREE.CylinderGeometry(0.1, 0.14, 1.2, 8);
  const trunk = new THREE.Mesh(trunkGeo, trunkMat);
  trunk.position.y = 0.6;
  trunk.castShadow = true;
  tree.add(trunk);

  // Round puffy canopy (cluster of spheres)
  const puffs: [number, number, number, number][] = [
    [0, 1.8, 0, 0.6],
    [-0.25, 1.6, 0.2, 0.45],
    [0.3, 1.7, -0.15, 0.4],
    [0, 1.5, -0.3, 0.35],
  ];
  puffs.forEach(([x, y, z, r]) => {
    const pGeo = new THREE.SphereGeometry(r, 8, 8);
    const p = new THREE.Mesh(pGeo, canopyMat);
    p.position.set(x, y, z);
    p.castShadow = true;
    tree.add(p);
  });

  return tree;
}

/**
 * Animate environment elements (called from frame loop via exterior scene).
 */
export function updateEnvironmentAnimations(group: THREE.Group, elapsed: number): void {
  // Animate ducks (gentle bobbing)
  const ducks = group.getObjectByName('ducks');
  if (ducks) {
    ducks.children.forEach((duck) => {
      const offset = duck.userData.animOffset as number;
      duck.position.y = 0.08 + Math.sin(elapsed * 1.5 + offset) * 0.015;
      duck.rotation.y += 0.002;
    });
  }

  // Animate birds (wing flap + drift)
  const birds = group.getObjectByName('birds');
  if (birds) {
    birds.children.forEach((bird) => {
      const offset = bird.userData.animOffset as number;
      const speed = bird.userData.speed as number;
      // Gentle sine wave drift
      bird.position.x += Math.sin(elapsed * speed + offset) * 0.003;
      bird.position.z -= 0.005 * speed;
      // Reset if too far
      if (bird.position.z < -20) bird.position.z = 10;
      // Wing flap
      if (bird.children.length >= 2) {
        const flapAngle = Math.sin(elapsed * 6 + offset) * 0.4;
        bird.children[0].rotation.z = 0.3 + flapAngle;
        bird.children[1].rotation.z = -0.3 - flapAngle;
      }
    });
  }

  // Animate clouds (slow drift)
  const clouds = group.getObjectByName('clouds');
  if (clouds) {
    clouds.children.forEach((cloud) => {
      const offset = cloud.userData.animOffset as number;
      cloud.position.x += 0.003;
      cloud.position.y += Math.sin(elapsed * 0.2 + offset) * 0.001;
      if (cloud.position.x > 20) cloud.position.x = -20;
    });
  }
}
